import { defineComponent as _defineComponent } from 'vue'
import {
  computed,
  inject,
  onMounted,
  onBeforeUnmount,
  PropType,
  ref,
} from "vue";
import NavigationBar from "../shared/NavigationBar.vue";
import {
  IBusLocation,
  ILineInfo,
  IPath,
  NAVERMAP_POLYLINE_STYLES,
} from "@/domain/entities/lineInfo";
import { PROVIDE_KEY_SHOW_TOAST_MESSAGE, ShowToastMessage } from "@/types";
import { IBusStop } from "@/domain/entities/busStop";
import LineTurnPathLegend from "./LineTurnPathLegend.vue";
import { ILinePathMapColorInfo } from "@/domain/entities/serviceInfo/serviceInfo.types";
import { useServiceStore } from "@/stores/serviceStore";

import MapZoomLevelSetter from "./MapZoomLevelSetter.vue";
import { useZoomLevel } from "@/composables/map/useZoomLevel";
import { useCreateBusStopMarkers } from "@/composables/map/useCreateBusStopMarkers";
import { useNaverMapPaths } from "@/composables/map/useNaverMapPaths";
import { useBusLocationMarkers } from "@/composables/map/useBusLocationMarkers";
import dayjs from "dayjs";
import getPathsByDate from "@/use/useGetPathsByDate";
import getBusStopsByDate from "@/use/useGetBusStopsByDate";


const __default__ = {
  name: "MultipleLineTurnPathMap",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
  lineInfos: {
    type: Array as PropType<ILineInfo[]>,
    default: () => [],
  },
  linePathColorInfos: {
    type: Array as PropType<ILinePathMapColorInfo[]>,
    default: () => [],
  },
  busLocations: {
    type: Array as PropType<IBusLocation[]>,
    default: () => [],
  },
  showAllBusStopNames: {
    type: Boolean,
    default: false,
  },
},
  emits: ["onClickClose", "onClickRefreshBtn"],
  setup(__props, { expose: __expose, emit: __emit }) {
  __expose();

const props = __props;

const emit = __emit;

const refreshButton = ref();
const isRotating = ref(false);

const emitOnClickRefreshBtn = () => {
  if (isRotating.value) return;
  isRotating.value = true;

  refreshButton.value?.classList.add("rotate");
  setTimeout(() => {
    refreshButton.value?.classList.remove("rotate");
    isRotating.value = false;
  }, 500);

  emit("onClickRefreshBtn");
};

const showToastMessage = inject(
  PROVIDE_KEY_SHOW_TOAST_MESSAGE
) as ShowToastMessage;

const serviceStore = useServiceStore();
const shouldShowTurnPath = serviceStore.serviceInfo?.shouldShowTurnPath;

const title = computed(() => (shouldShowTurnPath ? "경로보기" : "지도보기"));

/** 선택된 노선들의 경로를 배열로 합침 */
const getLinesPathList = () => {
  const pathList: IPath[][] = [];

  props.lineInfos.forEach((line) => {
    const firstTurn = line.turns[0];
    const today = dayjs().format("YYYY-MM-DD HH:mm");
    const paths = firstTurn ? getPathsByDate(firstTurn, today) : [];

    if (paths?.length) {
      pathList.push(paths);
    } else {
      pathList.push([]);
    }
  });

  return pathList;
};

/** 선택된 노선들의 정류장을 배열로 합침 */
const getLinesBusStopList = () => {
  const busStopList: IBusStop[][] = [];

  props.lineInfos.forEach((line) => {
    const firstTurn = line.turns[0];
    const today = dayjs().format("YYYY-MM-DD HH:mm");
    const busStops = firstTurn ? getBusStopsByDate(firstTurn, today) : [];

    busStopList.push(busStops);
  });

  return busStopList;
};

/** 선택된 노선들의 경로 정보 */
const linesPathList = getLinesPathList();

/** 선택된 노선들의 정류장 정보 */
const linesBusStopList = getLinesBusStopList();

/** 지도 Zoom Level */
const MIN_ZOOM_LEVEL = 8;
const MAX_ZOOM_LEVEL = 18;

const {
  zoomLevel,
  increaseZoomLevel,
  decreaseZoomLevel,
  addZoomChangedNaverEventListener,
  removeZoomChangedNaverEventListener,
} = useZoomLevel({
  minZoom: MIN_ZOOM_LEVEL,
  maxZoom: MAX_ZOOM_LEVEL,
});

/** 경로 그리기 */
const { drawNaverMapPaths } = useNaverMapPaths();

/** 정류장 마커 그리기 */
const { createBusStopMarkers, updateSelectedBusStopMarkerWithInfoWindow } =
  useCreateBusStopMarkers();

/** 실시간 버스 위치 그리기 */
const {
  busLocationMarkers,
  createBusLocationMarker,
  createBusLocationMarkers,
  removeBusLocationMarkers,
} = useBusLocationMarkers();

const mapContainer = ref<HTMLElement>();
let map: any = undefined;

const selectedBusStopId = ref<string | undefined>(undefined);

const handleBusStopClick = (busStop: any) => {
  if (selectedBusStopId.value === busStop.id) {
    selectedBusStopId.value = undefined;
  } else {
    selectedBusStopId.value = busStop.id;
  }
  updateSelectedBusStopMarkerWithInfoWindow(
    selectedBusStopId.value,
    props.showAllBusStopNames
  );
};

const addScript = () => {
  const naverMapScript = document.createElement("script");
  naverMapScript.setAttribute(
    "src",
    `https://oapi.map.naver.com/openapi/v3/maps.js?ncpKeyId=${process.env.VUE_APP_NAVER_CLIENT_ID}&submodules=panorama,drawing`
  );
  naverMapScript.async = true;
  document.head.appendChild(naverMapScript);
  naverMapScript.onload = () => initMap();
};

const initMap = () => {
  const center = getCenterOfPaths(linesPathList);

  map = new window.naver.maps.Map("map", {
    center: new window.naver.maps.LatLng(center.latitude, center.longitude),
    zoom: zoomLevel.value,
    mapTypes: new window.naver.maps.MapTypeRegistry({
      normal: new window.naver.maps.NaverStyleMapTypeOptions.getNormalMap({
        overlayType: "bg.ol.ts.lko",
      }),
    }),
    minZoom: MIN_ZOOM_LEVEL,
    maxZoom: MAX_ZOOM_LEVEL,
  });

  linesBusStopList.forEach((busStops, index) => {
    createBusStopMarkers(
      map,
      busStops,
      props.linePathColorInfos[index].markerCdn,
      handleBusStopClick
    );
  });

  if (props.showAllBusStopNames) {
    updateSelectedBusStopMarkerWithInfoWindow(undefined, true);
  }

  linesPathList.forEach((paths, index) => {
    drawNaverMapPaths(map, {
      paths,
      strokeColor: props.linePathColorInfos[index].color,
      strokeStyle:
        NAVERMAP_POLYLINE_STYLES[index % NAVERMAP_POLYLINE_STYLES.length],
    });
  });

  // createBusLocationMarkers(map);
  addZoomChangedNaverEventListener(map);
};

const getCenterOfPaths = (pathsList: IPath[][]): IPath => {
  if (!pathsList.length) {
    const firstBusStop = linesBusStopList?.[0]?.[0];
    return {
      latitude: firstBusStop?.latitude ?? 37.4044893,
      longitude: firstBusStop?.longitude ?? 127.0982259,
    };
  }

  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;

  pathsList.forEach((paths) => {
    paths.forEach((path) => {
      if (path.latitude < minLat) minLat = path.latitude;
      if (path.latitude > maxLat) maxLat = path.latitude;
      if (path.longitude < minLng) minLng = path.longitude;
      if (path.longitude > maxLng) maxLng = path.longitude;
    });
  });

  return {
    latitude: (minLat + maxLat) / 2,
    longitude: (minLng + maxLng) / 2,
  };
};

const toastErrorMessage = computed(() => {
  if (shouldShowTurnPath && !linesPathList.length) {
    return "경로 정보가 존재하지 않습니다.";
  }

  if (shouldShowTurnPath && linesPathList.length !== props.lineInfos.length) {
    return "경로 정보가 존재하지 않는 노선이 있습니다.";
  }

  // if (!props.busLocations.length) {
  //   return "현재 운행중인 버스 정보를 찾을 수 없습니다";
  // }

  return "";
});

const handleShowToastErrorMessage = () => {
  if (!toastErrorMessage.value) return;

  showToastMessage(toastErrorMessage.value);
};

onMounted(() => {
  window.naver ? initMap() : addScript();
  handleShowToastErrorMessage();
});

// const isSameBusLocation = (
//   newLocations: IBusLocation[],
//   oldLocations: IBusLocation[]
// ) => {
//   return newLocations.every((newLocation, index) => {
//     const targetOldLocation = oldLocations[index];
//     return (
//       newLocation.lat === targetOldLocation?.lat &&
//       newLocation.lng === targetOldLocation?.lng
//     );
//   });
// };

// watch(
//   () => props.busLocations,
//   (newBusLocations, oldBusLocations) => {
//     if (isSameBusLocation(newBusLocations, oldBusLocations)) return;
//     removeBusLocationMarkers();
//     createBusLocationMarkers(map);
//   }
// );

// showAllBusStopNames prop 변경 시 마커 업데이트
// watch(
//   () => props.showAllBusStopNames,
//   (newValue) => {
//     // showAllBusStopNames 변경 시 항상 모든 마커를 업데이트
//     updateSelectedBusStopMarkerWithInfoWindow(
//       selectedBusStopId.value,
//       newValue
//     );
//   }
// );

onBeforeUnmount(() => {
  removeZoomChangedNaverEventListener();
});

const __returned__ = { props, emit, refreshButton, isRotating, emitOnClickRefreshBtn, showToastMessage, serviceStore, shouldShowTurnPath, title, getLinesPathList, getLinesBusStopList, linesPathList, linesBusStopList, MIN_ZOOM_LEVEL, MAX_ZOOM_LEVEL, zoomLevel, increaseZoomLevel, decreaseZoomLevel, addZoomChangedNaverEventListener, removeZoomChangedNaverEventListener, drawNaverMapPaths, createBusStopMarkers, updateSelectedBusStopMarkerWithInfoWindow, busLocationMarkers, createBusLocationMarker, createBusLocationMarkers, removeBusLocationMarkers, mapContainer, get map() { return map }, set map(v) { map = v }, selectedBusStopId, handleBusStopClick, addScript, initMap, getCenterOfPaths, toastErrorMessage, handleShowToastErrorMessage, NavigationBar, LineTurnPathLegend, MapZoomLevelSetter }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})